import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { sendOTP, verifyOTP } from '../services/whatsappService';
import { api } from '../services/apiService';
import Footer from '../components/Footer';
import Header from '../components/Header';
import './AuthPage.css';

const AuthPage = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    if (!phoneNumber.trim()) {
      setError('Please enter a phone number');
      return;
    }

    // Format phone number (add + if not present)
    let formattedPhone = phoneNumber.trim();
    if (!formattedPhone.startsWith('+')) {
      formattedPhone = '+91' + formattedPhone;
    }

    setLoading(true);
    try {
      const result = await sendOTP(formattedPhone);
      
      if (result.success) {
        setSuccess(result.message);
        setStep('otp');
        // In development, show OTP in console
        if (result.otp) {
          console.log('Development OTP:', result.otp);
        }
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!otp.trim() || otp.trim().length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }

    setLoading(true);
    try {
      let formattedPhone = phoneNumber.trim();
      if (!formattedPhone.startsWith('+')) {
        formattedPhone = '+91' + formattedPhone;
      }

      const result = await verifyOTP(formattedPhone, otp.trim());

      if (result.success) {
        setSuccess('OTP verified successfully! Redirecting...');

        // Upsert user in backend
        let userId = null;
        try {
          const usersResp = await api.get('/users', { phone_number: formattedPhone });
          const existing = usersResp.data?.[0];
          if (existing) {
            userId = existing.id;
          } else {
            const created = await api.post('/users', { phone_number: formattedPhone });
            userId = created.data?.id;
          }
        } catch (apiErr) {
          console.warn('Backend user upsert failed:', apiErr.message);
        }

        // Record auth session
        try {
          const storedOtp = sessionStorage.getItem('pendingOTP') || otp.trim();
          await api.post('/auth-sessions', {
            phone_number: formattedPhone,
            otp: storedOtp,
            otp_expiry: Date.now() + 600000,
            verified: true,
          });
        } catch (apiErr) {
          console.warn('Auth session record failed:', apiErr.message);
        }

        login(formattedPhone, userId);
        setTimeout(() => {
          navigate('/home');
        }, 1000);
      } else {
        setError(result.message);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToPhone = () => {
    setStep('phone');
    setOtp('');
    setError('');
    setSuccess('');
  };

  return (
    <div className="auth-page">
      <div className="auth-container">
        <Header />

        <div className="auth-card">
          {step === 'phone' ? (
            <form onSubmit={handlePhoneSubmit} className="auth-form">
              <h2>Welcome</h2>
              <p className="form-subtitle">Enter your phone number to continue</p>
              
              <div className="form-group">
                <label htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="9645678900"
                  disabled={loading}
                  required
                />
                <small className="input-hint">Do not include country code (Currently available only for Indian numbers)</small> 
              </div>

              {error && <div className="alert alert-error">{error}</div>}
              {success && <div className="alert alert-success">{success}</div>}

              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Sending OTP...' : 'Send OTP via WhatsApp'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleOTPSubmit} className="auth-form">
              <h2>Verify OTP</h2>
              <p className="form-subtitle">
                We've sent a 6-digit code to {phoneNumber} via WhatsApp
              </p>
              
              <div className="form-group">
                <label htmlFor="otp">Enter OTP</label>
                <input
                  type="text"
                  id="otp"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength="6"
                  disabled={loading}
                  required
                  autoFocus
                />
                <small className="input-hint">Enter the 6-digit code you received</small>
              </div>

              {error && <div className="alert alert-error">{error}</div>}
              {success && <div className="alert alert-success">{success}</div>}

              <div className="form-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={handleBackToPhone}
                  disabled={loading}
                >
                  Change Number
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Verifying...' : 'Verify OTP'}
                </button>
              </div>
            </form>
          )}
        </div>

        <div className="auth-footer">
          <p>Don't have an account? <a href="/public">Continue as guest</a></p>
        </div> 
        <Footer />        
      </div>                 
    </div>
    
  );
};

export default AuthPage;

